import { useEffect, useState } from "react";
import {
  createFileRoute,
  Link,
  Outlet,
  useRouter,
} from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { buildHead } from "@/lib/seo";

const GOLD = "#C8A84B";

export const Route = createFileRoute("/portal")({
  head: () =>
    buildHead({
      locale: "en",
      title: "Client portal · ShugertGEO",
      description: "Track your GEO Readiness Audit orders and download finished reports.",
      path: "/portal",
      altPath: "/portal",
      noindex: true,
    }),
  component: PortalLayout,
});

function PortalLayout() {
  const router = useRouter();
  const [email, setEmail] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setEmail(data.session?.user.email ?? null);
      setHydrated(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setEmail(session?.user.email ?? null);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (hydrated && !email) {
      router.navigate({ to: "/login" });
    }
  }, [hydrated, email, router]);

  const signOut = async () => {
    await supabase.auth.signOut();
    router.navigate({ to: "/login" });
  };

  if (!hydrated) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!email) {
    // Redirect effect above will navigate to /login; show fallback briefly.
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground">Redirecting to sign in…</p>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-background">
      <header className="border-b border-border bg-card/30">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-4 px-6 py-4">
          <Link to="/portal" className="font-display text-lg font-semibold text-foreground">
            Shugert<span style={{ color: GOLD }}>GEO</span> · Portal
          </Link>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <Link to="/" className="hidden hover:text-foreground sm:inline">
              View site →
            </Link>
            <span className="hidden sm:inline">{email}</span>
            <button
              onClick={signOut}
              className="rounded-full border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-card"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-6 py-10">
        <Outlet />
      </main>
    </div>
  );
}